import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { setNotificationMessage } from 'redux/notifications/slice'
import { MAX_FILES_SIZE } from 'constants/files'
import {
  UPLOAD_FILE_DIMENSION_ERROR,
  UPLOAD_FILE_SIZE_ERROR,
} from 'texts/uiTexts'
import { checkImageDimension } from 'ui/atoms/UploadFile/utils'
import { Format } from 'ui/atoms/UploadFile/types'
import { PROFILE_DETAILS } from 'texts/profileDetails'

export type ValidationResult = {
  isValid: boolean
  errorMessage?: string
}

export type FileValidationOptions = {
  formats?: Format[]
  maxSize?: number
  checkDimensions?: boolean
  limitSizeByFile?: boolean
  withNotification?: boolean
}

export const useFileValidation = () => {
  const dispatch = useDispatch()

  const validateFiles = useCallback(
    async (
      files: File | File[],
      options: FileValidationOptions = {}
    ): Promise<ValidationResult> => {
      const {
        formats,
        maxSize = MAX_FILES_SIZE,
        checkDimensions = false,
        limitSizeByFile = true,
        withNotification = false,
      } = options

      const filesArray = Array.isArray(files) ? files : [files]
      const isSingleFile = !Array.isArray(files)

      if (formats && formats.length > 0) {
        const validFiles = filesArray.filter((file) =>
          formats.some((format) => file.name.toLowerCase().endsWith(format))
        )

        if (filesArray.length !== validFiles.length) {
          const errorMessage = PROFILE_DETAILS.UPLOAD_FILE.SIZE_ERROR
          dispatch(
            setNotificationMessage({
              notificationMessage: errorMessage,
              type: 'error',
            })
          )
          return { isValid: false, errorMessage }
        }
      }

      if (isSingleFile) {
        if (filesArray[0].size > maxSize) {
          const errorMessage = PROFILE_DETAILS.UPLOAD_FILE.SIZE_ERROR
          withNotification &&
            dispatch(
              setNotificationMessage({
                notificationMessage: errorMessage,
                type: 'error',
              })
            )
          return { isValid: false, errorMessage }
        }
      } else {
        const totalSize = filesArray.reduce((size, file) => size + file.size, 0)
        const sizeLimit = limitSizeByFile
          ? maxSize
          : maxSize * filesArray.length

        if (totalSize > sizeLimit) {
          const errorMessage = UPLOAD_FILE_SIZE_ERROR
          withNotification &&
            dispatch(
              setNotificationMessage({
                notificationMessage: errorMessage,
                type: 'error',
              })
            )
          return { isValid: false, errorMessage }
        }
      }
      if (checkDimensions) {
        for (const file of filesArray) {
          if (file.type.startsWith('image/')) {
            const isValidDimension = await checkImageDimension(file)
            if (!isValidDimension) {
              const errorMessage = UPLOAD_FILE_DIMENSION_ERROR
              withNotification &&
                dispatch(
                  setNotificationMessage({
                    notificationMessage: errorMessage,
                    type: 'error',
                  })
                )
              return { isValid: false, errorMessage }
            }
          }
        }
      }

      return { isValid: true }
    },
    [dispatch]
  )

  return {
    validateFiles,
  }
}
